import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, Button, Drawer, Spin, Switch, Typography } from 'antd';

import Truncate from 'components/Truncate';
import type { IrregularProduct } from 'scenes/Producer/Product/MyProducts/constants';

import { Icon } from '@eduzz/ui-antd-theme';

import type { QuickviewProps } from './constants';

const getActionsMessage = (product: IrregularProduct) => {
  const { pending_actions } = product;
  const messages: React.ReactNode[] = [];

  if (pending_actions.above_commission_limit) {
    messages.push(
      <div key='above_commission_limit' className='pb-2'>
        <Alert
          type='warning'
          message='Os produtos listados apresentam comissões que ultrapassam indevidamente o limite de 100%. Para evitar que esse valor adicional seja descontado das próximas transações, normalize esse cenário até 19/02.'
        />
      </div>
    );
  }

  if (pending_actions.above_coproducers_limit) {
    messages.push(
      <div key='above_coproducers_limit' className='pb-2'>
        <Alert
          type='warning'
          message='Os produtos listados abaixo contam com mais de 14 coprodutores cadastrados, sendo necessário a remoção dos excedentes até o dia 19/02. Por gentileza, regularize os cadastros'
        />
      </div>
    );
  }

  if (pending_actions.commission_by_value) {
    messages.push(
      <div key='commission_by_value' className='pb-2'>
        <Alert
          type='warning'
          message='Os produtos listados abaixo estão configurados com a regra de comissão fixa por valor, a qual deixará de existir a partir do dia 19/02. Por gentileza, adapte para a regra de comissionamento percentual.'
        />
      </div>
    );
  }

  if (pending_actions.invalid_physical_dimensions) {
    messages.push(
      <div key='invalid_physical_dimensions' className='pb-2'>
        <Alert
          type='warning'
          message='Os produtos listados abaixo estão configurados com dimensões que não são aceitas pelos correios. Por gentileza, corrija as dimensões dos produtos.'
        />
      </div>
    );
  }

  return messages;
};

const getEditButton = (product: IrregularProduct) => {
  const { id, parent_product } = product;
  const baseRoute = '/producer/product/editproduct';
  const finalRoute = parent_product
    ? `${baseRoute}/${parent_product}/${id}/coproducers`
    : `${baseRoute}/${id}/coproducers`;

  return (
    <Link to={finalRoute} className='block w-full'>
      <Button
        id={`my-products-irregular-edit-${id}`}
        block
        color='default'
        variant='filled'
        icon={<Icon.Pencil size={16} />}
      >
        Editar
      </Button>
    </Link>
  );
};

const QuickView: React.FC<QuickviewProps> = ({ active, changeActive, productData }) => {
  const [loading, setLoading] = useState(true);
  const [idProductHistory, setIdProductHistory] = useState<number>(null);
  const [statusProduct, setStatusProduct] = useState<'active' | 'inactive' | 'filed' | ' '>(null);

  const productDataRef = useRef(productData);
  productDataRef.current = productData;

  const prevActiveRef = useRef(active);

  const handleCloseQuickView = useCallback(() => {
    if (typeof changeActive === 'function') {
      changeActive();
    }

    setLoading(true);
    setIdProductHistory(productDataRef.current?.id);
  }, [changeActive]);

  useEffect(() => {
    const wasInactive = prevActiveRef.current === false;
    prevActiveRef.current = active;

    if (!wasInactive || !active) {
      return;
    }

    if (productData.id && productData.id !== idProductHistory) {
      setLoading(true);
      setLoading(false);
      setStatusProduct(productData.status);
    } else {
      setLoading(false);
    }
  }, [active, productData, idProductHistory]);

  const getToggleStatus = () => {
    switch (statusProduct) {
      case 'active':
        return 'Ativo';
      case 'inactive':
        return 'Inativo';
      case 'filed':
        return 'Arquivado';
      default:
        return 'Sem status';
    }
  };

  const labelToggleStatus = getToggleStatus();

  if (!productData) {
    return (
      <Drawer
        onClose={handleCloseQuickView}
        placement='right'
        open={false}
        closable={false}
        width='min(450px, 100vw)'
      />
    );
  }

  return (
    <Drawer
      onClose={handleCloseQuickView}
      placement='right'
      open={active}
      width='min(450px, 100vw)'
      title={`Produto: ${productData.id}`}
    >
      {loading ? (
        <div className='flex h-full items-center justify-center'>
          <Spin tip='Carregando produto ...' />
        </div>
      ) : (
        <div className='flex flex-col gap-4'>
          <div className='flex items-center gap-4'>
            {productData.image ? (
              <img src={productData.image} alt='' className='size-14 shrink-0 rounded-lg object-cover' />
            ) : (
              <div className='size-14 shrink-0 rounded-lg bg-(--uizz-antd-color-fill-tertiary)' />
            )}

            <div className='flex min-w-0 flex-1 flex-col gap-1'>
              <Typography.Text strong ellipsis title={productData.title ? productData.title : 'Produto desconhecido'}>
                <Truncate count={40} final={'...'}>
                  {productData.title ? productData.title : 'Produto desconhecido'}
                </Truncate>
              </Typography.Text>

              {!productData.has_children && (
                <div className='flex items-center gap-2'>
                  <Switch
                    size='small'
                    defaultChecked={statusProduct === 'active'}
                    checked={statusProduct === 'active'}
                    disabled={true}
                  />
                  <Typography.Text type='secondary'>{labelToggleStatus}</Typography.Text>
                </div>
              )}
            </div>
          </div>

          <div className='flex flex-col gap-2'>
            {getActionsMessage(productData)}
            {getEditButton(productData)}
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default QuickView;
